import { Button, CopyButton, Modal, Textarea } from '@mantine/core';
import { IconBrandWhatsapp, IconCopy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { trackPlanningSharedWhatsapp } from 'src/Services/Mixpanel/events';

import styles from './WhatsAppShareModal.module.scss';
import { Props } from './WhatsAppShareModal.types';

const WhatsAppShareModal = ({ open, onClose, assetName, subscribeUrl }: Props) => {
  const { t } = useTranslation();

  const defaultMessage = t('whatsappShare.defaultMessage', {
    defaultValue:
      "Bonjour ! Pour suivre le calendrier de {{assetName}} depuis votre téléphone, touchez ce lien : {{url}}\n\nVous pourrez l'ajouter en un clic à Google Agenda, Outlook ou l'agenda Apple. Les séjours et travaux se mettront à jour tout seuls.",
    assetName,
    url: subscribeUrl,
  });

  const [message, setMessage] = useState(defaultMessage);

  // Reset the message when the modal reopens with a different asset/url.
  useEffect(() => {
    if (open) setMessage(defaultMessage);
  }, [open, defaultMessage]);

  const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <div className={styles.title}>
          {t('whatsappShare.title', 'Envoyer le calendrier à la famille')}
        </div>
      }
      withCloseButton={true}
    >
      <div className={styles.container}>
        <div className={styles.intro}>
          {t(
            'whatsappShare.intro',
            "Le message s'ouvre dans WhatsApp, vous choisissez à qui l'envoyer. Chacun pourra ajouter le calendrier à son agenda sans créer de compte."
          )}
        </div>

        <Textarea
          value={message}
          onChange={event => setMessage(event.currentTarget.value)}
          autosize
          minRows={5}
          maxLength={1500}
        />

        <div className={styles.actions}>
          <Button
            component="a"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            leftSection={<IconBrandWhatsapp size={20} />}
            className={styles.openButton}
            onClick={() => trackPlanningSharedWhatsapp({ channel: 'whatsapp' })}
            fullWidth
          >
            {t('whatsappShare.open', 'Ouvrir WhatsApp')}
          </Button>

          <CopyButton value={message}>
            {({ copied, copy }) => (
              <Button
                variant="outline"
                leftSection={<IconCopy size={16} />}
                onClick={() => {
                  copy();
                  trackPlanningSharedWhatsapp({ channel: 'copy' });
                }}
                fullWidth
              >
                {copied
                  ? t('whatsappShare.copied', 'Message copié !')
                  : t('whatsappShare.copy', 'Copier le message')}
              </Button>
            )}
          </CopyButton>
        </div>
      </div>
    </Modal>
  );
};

export default WhatsAppShareModal;
